import { simboloMoneda } from './moneda';
// src/dominio/logicaMensajes.js
/**
 * Mensajes de WhatsApp que no dependen de una plantilla editable: pago por
 * link o pasarela, contacto con entrenadores, URL de wa.me y limpieza de
 * emojis. Los mensajes a clientes (bienvenida, recordatorios, pagos) salen de
 * las plantillas de cada gimnasio: ver dominio/plantillasMensajes.js.
 *
 * Cero imports de Firebase, React o servicios externos.
 */

// ═══════════════════════════════════════════════════════════════════════
//  CONSTANTES
// ═══════════════════════════════════════════════════════════════════════

/** @deprecated Usar codigoPais del GymContext. Se mantiene como fallback. */
export const CODIGO_PAIS  = '51';

// ═══════════════════════════════════════════════════════════════════════
//  MENSAJES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Construye el mensaje para enviar datos de pago digital al cliente.
 *
 * @param {Object} params
 * @param {string} params.nombre       - Nombre del cliente (primer nombre)
 * @param {number|string} params.monto - Monto a pagar
 * @param {string} params.pasarelaPago - Tipo: 'yape'|'link_manual'|'izipay'|'mercadopago'
 * @param {string} params.urlPasarela  - URL o número de la pasarela
 * @param {string} params.nombreGym    - Nombre del gimnasio
 * @returns {string}
 */
export const construirMensajePasarela = ({ nombre, monto, pasarelaPago, urlPasarela, nombreGym }) => {
  if (pasarelaPago === 'yape') {
    return (
      `¡Hola ${nombre}! 👋\n\n` +
      `Para tu pago de *${simboloMoneda()} ${monto}* en ${nombreGym}, ` +
      `puedes enviar por Yape/Plin al número:\n\n` +
      `📱 *${urlPasarela}*\n\n` +
      `Envíanos el comprobante por aquí. ¡Gracias! 💪`
    );
  }

  if (pasarelaPago === 'link_manual') {
    return (
      `¡Hola ${nombre}! 👋\n\n` +
      `Para tu pago de *${simboloMoneda()} ${monto}* en ${nombreGym}, ` +
      `puedes usar este link:\n\n` +
      `🔗 ${urlPasarela}\n\n` +
      `¡Gracias por tu preferencia! 💪`
    );
  }

  // izipay, mercadopago u otro
  return (
    `¡Hola ${nombre}! 👋\n\n` +
    `Tu pago de *${simboloMoneda()} ${monto}* en ${nombreGym} está listo.\n\n` +
    `Referencia: ${urlPasarela}\n\n` +
    `¡Gracias! 💪`
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  URL
// ═══════════════════════════════════════════════════════════════════════

/**
 * Mensaje simple de contacto para un entrenador.
 *
 * @param {Object} params
 * @param {string} params.nombre    - Nombre del entrenador
 * @param {string} params.nombreGym - Nombre del gimnasio
 * @returns {string}
 */
export const construirMensajeEntrenador = ({ nombre, nombreGym }) =>
  `¡Hola ${nombre}! 👋 Te contactamos desde *${nombreGym}*.`;

/**
 * Quita TODOS los emojis de un texto (y limpia los espacios que dejan).
 *
 * Por qué existe: cuando el mensaje se envía abriendo WhatsApp **Web/Desktop**
 * con un link wa.me pre-rellenado, WhatsApp Web corrompe los emojis de 4 bytes
 * (astrales, ej. 💪) y los transmite como "�" al cliente — incluso a su móvil.
 * Desde el móvil del que envía no pasa. Como no se puede arreglar WhatsApp Web,
 * en escritorio se manda el texto sin emojis (la UI decide cuándo llamar a esto).
 *
 * Conserva acentos, *negritas* de WhatsApp y saltos de línea. Escrito con
 * escapes ASCII a propósito (un literal de emoji en el fuente es frágil).
 *
 * @param {string} texto
 * @returns {string}
 */
export const quitarEmojis = (texto = '') =>
  texto
    .replace(/(\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}\u{1F3FB}-\u{1F3FF}\u{200D}\u{FE0F}\u{20E3}])/gu, '')
    .replace(/[^\S\n]{2,}/g, ' ')        // colapsa espacios dobles que deja el emoji
    .replace(/[^\S\n]+([\n.,!?])/g, '$1') // quita espacio antes de puntuación/salto
    .replace(/[^\S\n]+$/gm, '')          // limpia espacios al final de cada línea
    .trim();

/**
 * Construye la URL de WhatsApp Web/App para enviar un mensaje.
 *
 * @param {string} telefono - Teléfono del cliente (se limpian no-dígitos)
 * @param {string} mensaje - Texto del mensaje
 * @param {string} [codigoPais='51'] - Código telefónico del país (desde configGym)
 * @returns {string|null} URL completa, o null si no hay teléfono
 */
export const construirUrlWhatsApp = (telefono, mensaje, codigoPais = CODIGO_PAIS) => {
  if (!telefono) return null;
  const tel = telefono.replace(/\D/g, '');
  if (!tel) return null;
  return `https://wa.me/${codigoPais}${tel}?text=${encodeURIComponent(mensaje)}`;
};
